/**
 * NichtPortfolioTabelle (D-075, „Punkt D") — die Gesellschaften, die im Snapshot
 * stehen, aber NICHT zum Portfolio gehören (Curare, Gewerbeenergien).
 *
 * WARUM SEPARAT und nicht als weitere Zeilen der Gesellschafts-Tabelle:
 * `entities` ist seit D-075 nicht mehr das Portfolio. Wer diese Zahlen in dieselbe
 * Tabelle stellt, legt eine Summenbildung nahe, die falsch wäre — das Backend
 * schließt sie in `cumulative`/`quarterly`/`contribution` gerade aus.
 *
 * WARUM ANDERE SPALTEN (JÜ / EBTDA / Netto-CF / Bank):
 * Gewerbeenergien hat kein NKM-Konto; der Erzeuger liefert dort `nkm = 0.0`
 * (NICHT null — am Snapshot 30.07.2026 geprüft). Eine NKM-Spalte zeigte also
 * „0,00 €" — eine dargestellte Null ohne Aussage, schlechter als eine leere Zelle.
 * Es gibt aus demselben Grund KEINE Ampel-Spalte (der Soll-Maßstab VJ/4 deckt
 * diese Gesellschaften nicht).
 *
 * `bewertung` ist bei beiden `null` (`valuation_applicable = false`). Das wird als
 * „nicht anwendbar" AUSGEWIESEN, nicht stillschweigend weggelassen und niemals
 * als 0 (D-075).
 *
 * HARTE null-Regel (SCHEMA_MAPPING §5): null/fehlend → „—", NIE 0.
 */
import type { KeyboardEvent } from 'react';
import type { NonMemberKpi } from '../types/snapshot';
import { formatEur } from '../lib/format';
import { useJahre } from '../lib/jahre';

/**
 * SR-B-024 (Entscheidung Tom-Eric: Option 1 — rein textliche Fußnote, kein Datenfeld,
 * kein Schema-Bump).
 *
 * Überschrift, Untertitel und Fußnote stehen ABSICHTLICH in EINEM Objekt: die
 * Fußnote erklärt genau die Abgrenzung, die die Überschrift behauptet. Zwei
 * getrennte Strings wären zwei Quellen für eine Aussage — das ist Defektmuster (1),
 * dasselbe Muster, das bei A51/AA3/A134/A87/A105 fünfmal zugeschlagen hat.
 * Wer den Titel ändert, sieht die Fußnote daneben.
 */
export const NICHT_PORTFOLIO_TEXTE = {
  // D-084 (Prompt 127, Aufgabe 3): Abschnittstitel laut Vorgabe. Die Abgrenzung
  // („keine Portfolio-Summe") steht weiter im Untertitel und in der Fußnote.
  titel: 'Weitere Gesellschaften (nicht immobilienhaltend)',
  untertitel:
    'Im Snapshot enthalten, fließen aber in keine Portfolio-Summe ein. ' +
    'Zeile anklicken für die Detailansicht.',
  /**
   * Messgrundlage (SR-B-024, Snapshot 30.07., Q1-IST): 6 von 6 Portfolio-Gesellschaften
   * buchen Aufwand gegen die Gegenpartei CFS, und Curare IST die CFS. Von Curares
   * Ertragspositionen ≠ 0 stehen nur 4,0 % in den ausdrücklich „intern" benannten
   * Positionen — der Rest liegt in `nkm`, `erloese_nk`, `sonst_umsatz`, deren Namen
   * das Gegenteil suggerieren. Ohne diesen Satz legt die Tabelle eine externe
   * Umsatzgröße nahe, die es nicht ist.
   *
   * Wie hoch der Konzernanteil TATSÄCHLICH ist, lässt sich aus dem Snapshot nicht
   * bestimmen (SR-B-024, [Wissenslücke]) — der Satz nennt deshalb kein Verhältnis.
   */
  fussnote:
    'Die Erlöse dieser Gesellschaften sind überwiegend konzernintern und in den ' +
    'Portfolio-Summen bereits als Aufwand enthalten. Eine Addition mit den ' +
    'Portfolio-Erlösen doppelt.',
  bewertungHinweis: 'Bewertung / NAV ist hier nicht anwendbar (keine Immobilie im Bestand).',
} as const;

export interface NichtPortfolioTabelleProps {
  nonMembers?: NonMemberKpi[];
  /** D-084: slug der gewählten Gesellschaft (gemeinsame Auswahl der PortfolioPage). */
  selected?: string | null;
  /**
   * D-084: Auswahl → PortfolioPage lädt /api/asset/{slug} und rendert DASSELBE
   * AssetDetail wie für die Portfolio-Gesellschaften (ein Rendering-Pfad, keine
   * Kopie). Ohne Callback bleiben die Zeilen reine Tabellenzeilen.
   */
  onSelect?: (slug: string) => void;
}

export function NichtPortfolioTabelle({ nonMembers, selected, onSelect }: NichtPortfolioTabelleProps) {
  const { bj, vj } = useJahre(); // D-089; Hook vor dem frühen return (Rules of Hooks)
  // Kein Block, wenn es keine gibt — eine leere Tabelle behauptet, es gäbe eine
  // Abgrenzung, die im Snapshot nicht existiert. Ein älterer Backend-Stand ohne
  // das Feld führt hierher (undefined), nicht zu einer leeren Tabelle.
  if (!nonMembers?.length) return null;

  // „nicht anwendbar" nur ausweisen, wenn der Snapshot es auch sagt.
  const bewertungNichtAnwendbar = nonMembers.some((n) => n.valuation_applicable === false);

  return (
    <section aria-label={NICHT_PORTFOLIO_TEXTE.titel}>
      <h2 className="mb-1 text-lg font-semibold text-text">{NICHT_PORTFOLIO_TEXTE.titel}</h2>
      <p className="mb-3 text-xs text-text-muted">{NICHT_PORTFOLIO_TEXTE.untertitel}</p>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-text-muted">
              <th scope="col" className="px-4 py-2 font-medium">Gesellschaft</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">JÜ-Prognose</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">JÜ-VJ {vj}</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">EBTDA-Prognose</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">
                Netto-CF{' '}
                <span
                  aria-hidden="true"
                  className="font-normal text-text-muted"
                  title="Vorläufig – enthält die Tilgung (D-030)"
                >
                  ‡
                </span>
              </th>
              <th scope="col" className="px-4 py-2 text-right font-medium">
                Bank <span className="font-normal text-ampel-green">· frei</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {nonMembers.map((n) => (
              <tr
                key={n.short_name}
                // D-084: dasselbe Klick-/Tastaturmuster wie die Gesellschafts-Tabelle.
                {...(onSelect && {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `Gesellschaft ${n.short_name} anzeigen`,
                  onClick: () => onSelect(n.slug),
                  onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(n.slug);
                    }
                  },
                })}
                className={`border-b border-border last:border-0 ${
                  onSelect ? 'cursor-pointer hover:bg-surface-2' : ''
                } ${selected === n.slug ? 'bg-surface-2' : ''}`}
              >
                <th scope="row" className="px-4 py-3 text-left font-medium text-text">
                  {n.short_name}
                </th>
                <td className="px-4 py-3 text-right tabular-nums">{formatEur(n.ju_prognose)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatEur(n.ju_vj)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatEur(n.ebtda_prognose)}</td>
                <td
                  className={`px-4 py-3 text-right tabular-nums ${
                    typeof n.netto_cf_prognose === 'number' && n.netto_cf_prognose < 0
                      ? 'text-ampel-red'
                      : ''
                  }`}
                >
                  {formatEur(n.netto_cf_prognose)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatEur(n.bank_bestaende?.bank)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* SR-B-024: die Fußnote, die die Überschrift erklärt — aus derselben Quelle. */}
      <p className="mt-2 text-xs font-medium text-text">{NICHT_PORTFOLIO_TEXTE.fussnote}</p>
      {bewertungNichtAnwendbar && (
        <p className="mt-1 text-xs text-text-muted">{NICHT_PORTFOLIO_TEXTE.bewertungHinweis}</p>
      )}
      <p className="mt-1 text-xs text-text-muted">
        ‡ Netto-CF ist eine Jahres-Prognose {bj} und vorläufig: sie enthält die
        Tilgung (Jahres-Tilgung nach R4-Mix, gegen Darlehensspiegel noch ungeprüft –
        D-030).
      </p>
    </section>
  );
}
